/**
 * AddInstanceDialog — modal de criação manual de instância para tenants V1.
 *
 * Layout premium replicando o padrão visual do `ContactNoteEditorDialog`
 * (modal de anotações do chat): header com gradient sutil + ícone badge,
 * cards de provider em paleta preto/branco e footer com `bg-muted/10`.
 *
 * Apenas planos com `subscription_plans.billing_integration='integracao_v1'`
 * têm acesso ao fluxo — para os demais, `InstanceList` continua redirecionando
 * para a página de planos.
 *
 * @version 2.0.0
 * RELEASE: 2026-06-28 v2.0.0 - Refatoração visual premium: header em gradient,
 *                              ícone WhatsApp circular, remoção de exibição de
 *                              cota/budget (cards sem vagas ficam ocultos),
 *                              footer padrão `bg-muted/10`.
 * RELEASE: 2026-06-15 v1.0.0 - Versão inicial.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { showToast } from "@/lib/toast";
import { createLogger } from "@/lib/logger";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Plug,
  Plus,
  CheckCircle2,
  AlertCircle,
  MessageCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

const log = createLogger("AddInstanceDialog");

interface AddInstanceDialogProps {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (instanceId: string) => void;
}

interface ProviderQuotaRow {
  provider_id: string;
  provider_name: string;
  display_name: string | null;
  channel_type: string;
  allowed_qty: number;
  in_use_qty: number;
  available_qty: number;
}

export function AddInstanceDialog({
  tenantId,
  open,
  onOpenChange,
  onCreated,
}: AddInstanceDialogProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Carrega cota por provider. Mesmo backend; apenas a UI deixa de exibir.
  // ---------------------------------------------------------------------------
  const quotaQuery = useQuery({
    queryKey: ["tenant-instance-quota", tenantId],
    enabled: open && !!tenantId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ProviderQuotaRow[]> => {
      const { data, error } = await supabase.rpc(
        "get_tenant_instance_quota_by_provider" as never,
        { p_tenant_hint: tenantId } as never,
      );
      if (error) {
        log.error("Erro ao buscar cota por provider", { error });
        throw error;
      }
      return (data || []) as ProviderQuotaRow[];
    },
  });

  const createMut = useMutation({
    mutationFn: async (providerId: string): Promise<string> => {
      const { data, error } = await supabase.rpc(
        "tenant_create_instance_from_addon" as never,
        { p_provider_id: providerId, p_tenant_hint: tenantId } as never,
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: (instanceId) => {
      showToast.success("Instância criada com sucesso");
      queryClient.invalidateQueries({ queryKey: ["instances", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["tenant-instance-quota", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["resource-limits"] });
      onCreated?.(instanceId);
      onOpenChange(false);
      setSelected(null);
    },
    onError: (err: any) => {
      showToast.error(err?.message || "Falha ao criar instância");
    },
  });

  // Lista efetiva: somente providers liberados (`allowed_qty > 0`) e com vaga
  // (`available_qty > 0`). Sem exibir números/cota para o usuário.
  const availableProviders = useMemo(
    () =>
      (quotaQuery.data || []).filter(
        (r) => r.allowed_qty > 0 && r.available_qty > 0,
      ),
    [quotaQuery.data],
  );

  const hasAvailable = availableProviders.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl p-0 gap-0 overflow-hidden flex flex-col"
        hideCloseButton={false}
      >
        {/* ===== HEADER PREMIUM (padrão ContactNoteEditorDialog) ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                <Plug className="h-3.5 w-3.5 text-primary" />
              </div>
              Adicionar instância
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Selecione o provider disponível para criar uma nova instância de
              WhatsApp.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* ===== CORPO ===== */}
        <div className="px-6 py-4 space-y-2 max-h-[60vh] overflow-y-auto">
          {quotaQuery.isLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!quotaQuery.isLoading && !hasAvailable && (
            <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
              <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto" />
              <p className="text-sm font-medium text-foreground">
                Nenhum provider disponível
              </p>
              <p className="text-xs text-muted-foreground">
                Contrate o add-on "Instâncias Adicionais" na página de planos
                para liberar a criação de novas instâncias.
              </p>
            </div>
          )}

          {!quotaQuery.isLoading &&
            hasAvailable &&
            availableProviders.map((r) => {
              const isSelected = selected === r.provider_id;
              const isOficial = r.channel_type === "whatsapp_oficial";
              return (
                <button
                  type="button"
                  key={r.provider_id}
                  onClick={() => setSelected(r.provider_id)}
                  className={cn(
                    "w-full text-left rounded-lg border p-4 transition-all",
                    "flex items-center gap-3",
                    isSelected
                      ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                      : "border-border hover:bg-muted/30",
                  )}
                >
                  {/* Ícone circular WhatsApp — paleta sutil */}
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                    <MessageCircle className="h-5 w-5 text-foreground" />
                  </div>

                  {/* Conteúdo */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {r.display_name || r.provider_name}
                      </p>
                      <Badge
                        variant="outline"
                        className="text-[10px] font-normal text-muted-foreground border-border"
                      >
                        {isOficial ? "WhatsApp API Oficial" : "WhatsApp"}
                      </Badge>
                    </div>
                  </div>

                  {/* Indicador de seleção */}
                  {isSelected && (
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                  )}
                </button>
              );
            })}
        </div>

        {/* ===== FOOTER PREMIUM (padrão ContactNoteEditorDialog) ===== */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={createMut.isPending}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={() => selected && createMut.mutate(selected)}
            disabled={!selected || createMut.isPending}
          >
            {createMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <Plus className="h-4 w-4 mr-1.5" />
            )}
            Criar instância
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
